import { useQuery } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
import { CircleHelp, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { proposalCountsQuery } from '@/api/queries/proposals'
import { usePermissions } from '@/auth/usePermissions'
import { Logo } from '@/components/Logo'
import { Kbd } from '@/components/ui/kbd'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/cn'
import { NAV, type NavItem } from './nav'
import { useAppScope } from './useAppScope'

function isActive(pathname: string, to: NavItem['to']): boolean {
  if (to === '/') return pathname === '/'
  return pathname === to || pathname.startsWith(`${to}/`)
}

/**
 * The path the sidebar highlights. An event type opened from an application
 * (`/event-types/evt_…?app=app_…`) belongs to that application, so it lights
 * up Applications rather than Event types.
 */
function navPath(pathname: string, search: Record<string, unknown>): string {
  if (pathname.startsWith('/event-types/') && typeof search.app === 'string') return '/applications'
  return pathname
}

export function Sidebar({
  collapsed,
  canToggle,
  onToggle,
}: {
  collapsed: boolean
  canToggle: boolean
  onToggle: () => void
}) {
  const pathname = useRouterState({
    select: (s) => navPath(s.location.pathname, s.location.search as Record<string, unknown>),
  })
  const { can } = usePermissions()
  const { appId } = useAppScope()
  const counts = useQuery(proposalCountsQuery(appId))
  const badges: Record<NonNullable<NavItem['badge']>, number> = {
    proposals: counts.data?.open ?? 0,
  }
  return (
    <nav
      aria-label="Main"
      className={cn(
        'flex h-full shrink-0 flex-col border-r border-line bg-bg transition-[width] duration-150',
        collapsed ? 'w-[52px]' : 'w-[216px]',
      )}
    >
      <div
        className={cn('flex h-12 shrink-0 items-center', collapsed ? 'justify-center' : 'px-3.5')}
      >
        <Link
          to="/"
          aria-label="MailSmith dashboard"
          className="rounded-md focus-visible:outline-2 focus-visible:outline-focus"
        >
          <Logo collapsed={collapsed} />
        </Link>
      </div>
      <div className="flex-1 overflow-y-auto px-2 pt-2 pb-3">
        {NAV.map((group, gi) => {
          const items = group.items.filter((i) => !i.permission || can(i.permission))
          if (items.length === 0) return null
          return (
            <div key={gi} className={cn(gi > 0 && 'mt-4')}>
              {group.label &&
                (collapsed ? (
                  <div className="mx-2 mb-2 h-px bg-line" aria-hidden />
                ) : (
                  <div className="mb-1 px-2.5 text-xs font-medium text-ink-3">{group.label}</div>
                ))}
              <ul className="space-y-px">
                {items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      item={item}
                      active={isActive(pathname, item.to)}
                      collapsed={collapsed}
                      count={item.badge ? badges[item.badge] : undefined}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </div>
      <div
        className={cn(
          'shrink-0 space-y-px border-t border-line p-2',
          collapsed && 'flex flex-col items-center',
        )}
      >
        <HelpLink
          active={pathname === '/help' || pathname.startsWith('/help/')}
          collapsed={collapsed}
        />
        {canToggle && (
          <Tooltip
            content={
              <span className="flex items-center gap-2">
                {collapsed ? 'Expand' : 'Collapse'} sidebar{' '}
                <Kbd className="border-transparent bg-white/15 text-inherit">[</Kbd>
              </span>
            }
            side="right"
          >
            <button
              type="button"
              onClick={onToggle}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className={cn(
                'flex h-8 cursor-pointer items-center gap-2 rounded-md px-2 text-sm text-ink-3 hover:bg-surface-3 hover:text-ink focus-visible:outline-2 focus-visible:outline-focus',
                !collapsed && 'w-full',
              )}
            >
              {collapsed ? (
                <PanelLeftOpen className="size-4" />
              ) : (
                <PanelLeftClose className="size-4" />
              )}
              {!collapsed && 'Collapse'}
            </button>
          </Tooltip>
        )}
        {canToggle && !collapsed && (
          <Link
            to="/help/whats-new"
            className="block w-fit rounded-sm px-2 pt-1 font-mono text-xs text-ink-3 hover:text-ink focus-visible:outline-2 focus-visible:outline-focus"
            title="What’s new in this version"
          >
            v{__APP_VERSION__}
          </Link>
        )}
      </div>
    </nav>
  )
}

function NavLink({
  item,
  active,
  collapsed,
  count,
}: {
  item: NavItem
  active: boolean
  collapsed: boolean
  /** Items waiting, shown as a small brass count; nothing for 0. */
  count?: number
}) {
  const Icon = item.icon
  const link = (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group relative flex h-8 items-center gap-2.5 rounded-md text-sm font-medium transition-colors',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus',
        collapsed ? 'justify-center' : 'px-2.5',
        active
          ? 'bg-surface text-ink shadow-[0_0_0_1px_var(--line)]'
          : 'text-ink-2 hover:bg-surface-3/70 hover:text-ink',
      )}
    >
      {/* The temper line: a short brass bar marking where you are. */}
      <span
        aria-hidden
        className={cn(
          'absolute top-1.5 bottom-1.5 left-0 w-[3px] rounded-r-full bg-brass-line transition-opacity',
          active ? 'opacity-100' : 'opacity-0',
          collapsed && '-left-2',
        )}
      />
      <Icon
        className={cn(
          'size-4 shrink-0',
          active ? 'text-brass' : 'text-ink-3 group-hover:text-ink-2',
        )}
        strokeWidth={1.85}
      />
      {!collapsed && <span className="truncate">{item.label}</span>}
      {!!count &&
        (collapsed ? (
          <span
            aria-hidden
            className="absolute top-1 right-1 size-2 rounded-full bg-brass ring-2 ring-bg"
          />
        ) : (
          <span className="tabular ml-auto rounded-sm border border-brass-line/50 bg-brass-soft px-1.5 text-xs leading-[18px] font-medium text-brass">
            {count > 99 ? '99+' : count}
          </span>
        ))}
      {!!count && <span className="sr-only">, {count} waiting</span>}
    </Link>
  )
  if (!collapsed) return link
  return (
    <Tooltip content={count ? `${item.label} (${count})` : item.label} side="right">
      {link}
    </Tooltip>
  )
}

/** The one persistent entry point to the help center. `?` opens help for the current page. */
function HelpLink({ active, collapsed }: { active: boolean; collapsed: boolean }) {
  return (
    <Tooltip
      content={
        <span className="flex items-center gap-1.5">
          Help center
          <span className="opacity-60">·</span>
          <Kbd className="border-transparent bg-white/15 text-inherit">?</Kbd>
          <span className="opacity-80">this page</span>
        </span>
      }
      side="right"
    >
      <Link
        to="/help"
        aria-current={active ? 'page' : undefined}
        className={cn(
          'flex h-8 items-center gap-2 rounded-md px-2 text-sm focus-visible:outline-2 focus-visible:outline-focus',
          !collapsed && 'w-full',
          active
            ? 'bg-surface text-ink shadow-[0_0_0_1px_var(--line)]'
            : 'text-ink-3 hover:bg-surface-3 hover:text-ink',
        )}
      >
        <CircleHelp className={cn('size-4', active && 'text-brass')} />
        {!collapsed && 'Help'}
      </Link>
    </Tooltip>
  )
}
